// src/pages/reports/StockAlertsReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'

// Stock Alerts Filters Component
const StockAlertsFilters = ({ filters, onFiltersChange, onApply, onClear }) => { // eslint-disable-line no-unused-vars
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)

  console.log('🔥 StockAlertsFilters component rendered with filters:', filters)

  // Get current user and check role
  const currentUser = AuthService.getCurrentUser()
  const userRole = currentUser?.role?.toLowerCase()
  const isBranchRestricted = ['manager', 'warehouse'].includes(userRole)
  const userBranchId = currentUser?.branchId || currentUser?.branch?._id || currentUser?.branch
  
  // Auto-set branch filter for restricted users on mount
  useEffect(() => {
    if (isBranchRestricted && userBranchId && !filters?.branch) {
      console.log('🔒 Stock Alerts Report: Auto-setting branch for restricted user:', userBranchId)
      onFiltersChange({ ...filters, branch: userBranchId })
    }
  }, [isBranchRestricted, userBranchId])

  // Load branches from API
  useEffect(() => {
    const loadFilters = async () => {
      try {
        // Load branches
        const branchResponse = await BranchService.getAllBranches()
        console.log('✅ Stock Alerts Report: Branches response:', branchResponse)
        
        let branchesData = []
        if (branchResponse && branchResponse.success) {
          if (branchResponse.data && branchResponse.data.branches && Array.isArray(branchResponse.data.branches)) {
            branchesData = branchResponse.data.branches
          } else if (Array.isArray(branchResponse.data)) {
            branchesData = branchResponse.data
          }
        }
        
        // Include all active branches (outlets and warehouses)
        let activeBranches = branchesData.filter(branch => branch.isActive)
        console.log('🏪 Stock Alerts Report: Active branches (outlets & warehouses):', activeBranches.length, 'branches')
        
        // Filter to only user's branch if restricted
        if (isBranchRestricted && userBranchId) {
          activeBranches = activeBranches.filter(branch => branch._id === userBranchId)
          console.log('🔒 Stock Alerts Report: Filtered to user\'s branch only')
        }
        
        setBranches(activeBranches)
        
      } catch (error) {
        console.error('❌ Stock Alerts Report: Error loading filters:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadFilters()
  }, [])

  // Mock branches as fallback
  const mockBranches = useMemo(() => [
    { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo', type: 'outlet', isActive: true },
    { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo', type: 'outlet', isActive: true }
  ], [])

  // Status options
  const statusOptions = useMemo(() => [
    { value: 'low_stock', label: 'Low Stock' },
    { value: 'critical', label: 'Critical' },
    { value: 'out_of_stock', label: 'Out of Stock' }
  ], [])

  // Item type options
  const itemTypeOptions = useMemo(() => [
    { value: 'product', label: 'Product' },
    { value: 'raw_material', label: 'Raw Material' }
  ], [])

  // Display branches (real or mock)
  const displayBranches = branches.length > 0 ? branches : mockBranches

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    // Clear filters using BaseReportPage's onClear callback if available
    if (onClear) {
      onClear()
    }
    // Maintain branch restriction for managers/warehouse users
    const clearedFilters = isBranchRestricted ? { branch: userBranchId } : {}
    onFiltersChange(clearedFilters)
  }

  const hasActiveFilters = useMemo(() => {
    const hasNonBranchFilters = filters.status || filters.itemType
    const hasBranchFilter = filters.branch && (!isBranchRestricted || filters.branch !== userBranchId)
    return hasNonBranchFilters || hasBranchFilter
  }, [filters, isBranchRestricted, userBranchId])

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <div className="relative">
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Statuses</option>
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
        </div>

        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch {isBranchRestricted && <span className="text-xs text-gray-500">(locked to your branch)</span>}
          </label>
          <div className="relative">
            <select
              value={filters.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none ${
                isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              disabled={loading || isBranchRestricted}
            >
              <option value="">All Branches</option>
              {displayBranches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          {loading && <p className="text-xs text-gray-500 mt-1">Loading branches...</p>}
          {!loading && branches.length === 0 && <p className="text-xs text-amber-500 mt-1">Using default branches</p>}
        </div>

        {/* Item Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
          <div className="relative">
            <select
              value={filters.itemType || ''}
              onChange={(e) => handleFilterChange('itemType', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Types</option>
              {itemTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const StockAlertsReport = () => {
  const columns = [
    {
      key: 'itemId',
      label: 'Item ID',
      width: '120px'
    },
    {
      key: 'itemName',
      label: 'Item Name',
      width: '200px'
    },
    {
      key: 'itemType',
      label: 'Item Type',
      width: '120px',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value === 'product' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {value === 'product' ? 'Product' : 'Raw Material'}
        </span>
      )
    },
    {
      key: 'currentStock',
      label: 'Current Stock',
      width: '120px',
      render: (value) => (
        <span className="font-mono">{value}</span>
      )
    },
    {
      key: 'threshold',
      label: 'Threshold',
      width: '120px',
      render: (value) => (
        <span className="font-mono">{value}</span>
      )
    },
    {
      key: 'branchName',
      label: 'Branch Name',
      width: '150px'
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (value) => {
        const statusConfig = {
          low_stock: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Low Stock' },
          critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
          out_of_stock: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Out of Stock' }
        }
        const config = statusConfig[value] || statusConfig.low_stock
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        )
      }
    }
  ]

  return (
    <div className="space-y-6">
      {/* <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Alerts Report</h1>
          <p className="text-gray-600 mt-1">Monitor inventory levels and stock alerts across all branches</p>
        </div>
      </div> */}

      <BaseReportPage
        reportType="stockAlerts"
        columns={columns}
        title="Stock Alerts Report"
        dataKey="data"
        enableFilters={true}
        customFilterComponent={StockAlertsFilters}
        enableExport={true}
        enablePrint={true}
      />
    </div>
  )
}

export default StockAlertsReport